import React from 'react';
import { useTheme } from '@emotion/react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Skeleton,
  styled,
  useMediaQuery,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { FetchAllResources } from 'api/resources';
import ToolCard from './ToolCard';
import { navigateToDomain } from 'utils/index';
import { Primary } from 'ui-components/Buttons';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'grid',
  alignContent: 'space-between',
  borderRadius: 20,
  padding: '0.5rem',
  boxShadow: 'none',
  maxWidth: '22.375rem',
  border: `1px solid ${theme.palette.customColors?.b50}`,
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
}));

const ResourceCard = ({ resource }) => {
  const theme = useTheme();

  const handleView = () => {
    const baseUrl = process.env.REACT_APP_BACKRR_MAIN_URL || 'https://backrr.com';
    window.open(`${baseUrl}/resources/${resource.slug}`, '_blank');
  };

  return (
    <StyledCard>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ fontSize: 'clamp(1rem, 1.25vw, 1.25rem)', lineHeight: 1.3 }}
          >
            {resource.title}
          </Typography>
          {resource.price_display && (
            <Chip
              label={resource.price_display.formatted || `${resource.price_display.symbol}${resource.price_display.amount}`}
              size="small"
              sx={{
                ml: 1,
                flexShrink: 0,
                bgcolor: theme.palette.customColors?.b50 || '#EEF2FF',
                color: theme.palette.customColors?.b300 || '#3C3CC5',
                fontWeight: 700,
                fontSize: '0.8rem',
              }}
            />
          )}
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '0.9rem', lineHeight: 1.5 }}
        >
          {resource.short_description || resource.description}
        </Typography>
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2 }}>
        <Primary
          fullWidth
          sx={{ padding: '.7rem 1.25rem' }}
          onClick={handleView}
        >
          {resource.is_unlocked ? 'View Resource' : 'Unlock Resource'}
        </Primary>
      </CardActions>
    </StyledCard>
  );
};

const ResourceCardSkeleton = () => (
  <StyledCard>
    <CardContent>
      <Skeleton variant="text" width="70%" height={28} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="100%" />
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="60%" />
    </CardContent>
    <CardActions sx={{ px: 2, pb: 2 }}>
      <Skeleton variant="rounded" width="100%" height={42} />
    </CardActions>
  </StyledCard>
);

const ResourcesTab = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const investorDetails = useSelector((state) => state?.investorDetails?.data);
  const { data: resources, isLoading } = FetchAllResources(investorDetails?._id);

  const handleView = (resource) => {
    resource?.slug && window.open(navigateToDomain("main", "resources/" + resource?.slug + (resource?.is_unlocked ? "#resource" : "")))
  };

  // if (isLoading) {
  //   return (
  //     <Box
  //       sx={{
  //         display: 'grid',
  //         gridTemplateColumns: 'repeat(auto-fill, minmax(min(22.4375rem, 100%), 1fr))',
  //         gap: { xs: '2rem', sm: '3rem', md: '4rem' },
  //         padding: isSmallScreen ? '1rem' : '0 2rem 2rem 2rem',
  //         justifyItems: { xs: 'center', md: 'start' },
  //       }}
  //     >
  //       {[1, 2, 3].map((i) => <ResourceCardSkeleton key={i} />)}
  //     </Box>
  //   );
  // }

  if (!resources || resources.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '20rem',
          gap: 2,
          padding: '2rem',
        }}
      >
        <Typography variant="h6" color="text.secondary" fontWeight={600}>
          No Resources Available
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Check back soon for curated resources to help you invest smarter.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(22.4375rem, 100%), 1fr))',
        gap: { xs: '2rem', sm: '3rem', md: '4rem' },
        padding: isSmallScreen ? '1rem' : '0 2rem 2rem 2rem',
        justifyItems: { xs: 'center', md: 'start' },
      }}
    >
      {resources.map((resource) => (
        <ToolCard key={resource._id || resource.id || resource.slug} card={{ ...resource, image: resource?.cover_image }} onClick={() => handleView(resource)} button_text={"View Resource"} />
      ))}
    </Box>
  );
};

export default ResourcesTab;
